// Geolocation utilities for browser-based location detection

export interface UserLocation {
  lat: number;
  lng: number;
  accuracy: number;
}

/**
 * Request user's current location using browser Geolocation API
 */
export function requestUserLocation(): Promise<UserLocation> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
      },
      (error) => {
        const messages: Record<number, string> = {
          1: 'Permission denied',
          2: 'Position unavailable',
          3: 'Request timeout',
        };
        reject(new Error(messages[error.code] || 'Unknown error'));
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      },
    );
  });
}

/**
 * Request user's location with a permission check first
 */
export async function requestLocationWithPermissionCheck(): Promise<UserLocation> {
  // Check if permission is already granted
  const permission = await navigator.permissions?.query?.({ name: 'geolocation' });

  if (permission?.state === 'denied') {
    throw new Error('Geolocation permission denied by user');
  }

  return await requestUserLocation();
}

/**
 * Geocode a city/state to approximate coordinates (fallback)
 * Uses OpenStreetMap Nominatim API
 */
export async function geocodeCity(city: string, state: string): Promise<UserLocation> {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?city=${encodeURIComponent(city)}&state=${encodeURIComponent(state)}&country=USA&format=json&limit=1`,
    );

    if (!response.ok) throw new Error('Geocoding failed');

    const results = await response.json();
    if (!results.length) throw new Error('Location not found');

    const result = results[0];
    return {
      lat: parseFloat(result.lat),
      lng: parseFloat(result.lon),
      accuracy: 5000, // Approximate accuracy in meters
    };
  } catch (error) {
    console.error('[Geolocation] Error geocoding city:', error);
    throw error;
  }
}

/**
 * Reverse geocode coordinates to find the nearest city and state
 * Uses OpenStreetMap Nominatim API
 */
export async function reverseGeocodeCity(
  lat: number,
  lng: number,
): Promise<{ city: string; state: string; county?: string } | null> {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=10&addressdetails=1`,
    );

    if (!response.ok) throw new Error('Reverse geocoding failed');

    const result = await response.json();
    if (!result.address) throw new Error('No address found');

    const address = result.address;

    // Extract city and state from the response
    // Nominatim returns different address components for different regions
    const city = address.city || address.town || address.village || address.hamlet || '';
    const state = address.state || '';
    const county = address.county || '';

    if (!city || !state) {
      console.warn('[Geolocation] Incomplete address from reverse geocoding:', address);
      return null;
    }

    return { city, state, county };
  } catch (error) {
    console.error('[Geolocation] Error reverse geocoding:', error);
    return null;
  }
}

/**
 * Find the nearest city in the geographic database to a given coordinate
 * Returns the closest match or null if none found
 */
export async function findNearestCityInDatabase(
  lat: number,
  lng: number,
  cities: Array<{ name: string; state: string; county: string; coordinates?: { lat: number; lng: number } }>,
): Promise<{ city: string; state: string; county: string } | null> {
  if (!cities.length) return null;

  // Calculate distances to all cities
  const citiesWithDistance = cities
    .filter((city) => city.coordinates)
    .map((city) => {
      const cityLat = city.coordinates!.lat;
      const cityLng = city.coordinates!.lng;
      const dLat = toRad(cityLat - lat);
      const dLng = toRad(cityLng - lng);

      const sinDLat2 = Math.sin(dLat / 2);
      const sinDLng2 = Math.sin(dLng / 2);
      const a = sinDLat2 * sinDLat2 + Math.cos(toRad(lat)) * Math.cos(toRad(cityLat)) * sinDLng2 * sinDLng2;
      const angle = 2 * Math.asin(Math.sqrt(a));
      const distanceMiles = 3959 * angle; // Earth's radius in miles

      return { city: city.name, state: city.state, county: city.county, distance: distanceMiles };
    })
    .sort((a, b) => a.distance - b.distance);

  // Return the closest city if within reasonable distance (50 miles)
  if (citiesWithDistance.length > 0 && citiesWithDistance[0].distance <= 50) {
    return {
      city: citiesWithDistance[0].city,
      state: citiesWithDistance[0].state,
      county: citiesWithDistance[0].county,
    };
  }

  return null;
}

/**
 * Helper function to convert degrees to radians
 */
function toRad(degrees: number): number {
  return degrees * (Math.PI / 180);
}
